// Labels and formats of the Rollo section (design README "Contenido y tono"): Argentine formats, Spanish
// labels for the server codes and words or a dash for missing data, never an empty cell.

export const EMPTY = "—";

export const ESTADO_LABELS = {
  SOLICITADO: "Solicitado",
  EN_PREPARACION: "En preparación",
  EN_CAMINO: "En camino",
  ENTREGADO: "Entregado",
  CANCELADO: "Cancelado",
};

// Chip tone of each estado (StatusChip tokens status-<tone>-bg / -ink).
export const ESTADO_TONES = {
  SOLICITADO: "requested",
  EN_PREPARACION: "preparing",
  EN_CAMINO: "onway",
  ENTREGADO: "delivered",
  CANCELADO: "cancelled",
};

export const CANAL_LABELS = {
  WHATSAPP: "WhatsApp",
  LLAMADA: "Llamada",
  PRESENCIAL: "Presencial",
  EMAIL: "Email",
};

export const ORIGEN_LABELS = {
  ASESOR: "Asesor",
};

// The backend only assigns ALTA and MEDIA today; CRITICA and BAJA are styled because the design defines them.
export const PRIORIDAD_LABELS = {
  CRITICA: "Crítica",
  ALTA: "Alta",
  MEDIA: "Media",
  BAJA: "Baja",
};

// PriorityChip tone and number of lit bars.
export const PRIORIDAD_LEVELS = {
  CRITICA: { tone: "critical", bars: 4 },
  ALTA: { tone: "high", bars: 3 },
  MEDIA: { tone: "medium", bars: 2 },
  BAJA: { tone: "low", bars: 1 },
};

export const MOTIVO_CANCELACION_LABELS = {
  YA_NO_LO_NECESITA: "Ya no lo necesita",
  PEDIDO_DUPLICADO: "Pedido duplicado",
  COMERCIO_DADO_DE_BAJA: "Comercio dado de baja",
  OTRO: "Otro",
};

export const MOTIVO_NO_ENTREGA_LABELS = {
  COMERCIO_CERRADO: "Comercio cerrado",
  DIRECCION_INCORRECTA: "Dirección incorrecta",
  NADIE_PARA_RECIBIR: "Nadie para recibir",
  OTRO: "Otro",
};

const normalizeCode = (code) => String(code ?? "").trim().toUpperCase();

export const labelFor = (labels, code) => {
  const raw = String(code ?? "").trim();
  if (!raw) return EMPTY;
  return labels[normalizeCode(raw)] || raw;
};

export const codeOf = (code) => normalizeCode(code);

const pad = (value) => String(value).padStart(2, "0");
const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})/;

// Server dates come without time zone ("2026-10-12T09:30:00"): read the calendar day from the text so the
// browser time zone never shifts it.
const dateParts = (value) => {
  if (value === null || value === undefined || value === "") return null;

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    return { year: String(value.getFullYear()), month: pad(value.getMonth() + 1), day: pad(value.getDate()) };
  }

  const match = ISO_DATE.exec(String(value));
  if (match) {
    return { year: match[1], month: match[2], day: match[3] };
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : dateParts(parsed);
};

export const formatDate = (value) => {
  const parts = dateParts(value);
  return parts ? `${parts.day}/${parts.month}/${parts.year}` : EMPTY;
};

export const formatShortDate = (value) => {
  const parts = dateParts(value);
  return parts ? `${parts.day}/${parts.month}` : EMPTY;
};

// "YYYY-MM-DD" of a value, for comparisons and date inputs.
export const toIsoDate = (value) => {
  const parts = dateParts(value);
  return parts ? `${parts.year}-${parts.month}-${parts.day}` : null;
};

export const todayIso = (now = new Date()) => toIsoDate(now);

// "HH:MM" of a server date-time, read from the text like the calendar day; null when it has no time.
const ISO_TIME = /T(\d{2}):(\d{2})/;
export const formatTime = (value) => {
  const match = ISO_TIME.exec(String(value ?? ""));
  return match ? `${match[1]}:${match[2]}` : null;
};

export const formatCuit = (value) => {
  const raw = String(value ?? "").trim();
  if (!raw) return EMPTY;

  const digits = raw.replace(/\D/g, "");
  if (digits.length === 11) {
    return `${digits.slice(0, 2)}-${digits.slice(2, 10)}-${digits.slice(10)}`;
  }

  return raw;
};

const wholeDays = (days) => Math.max(0, Math.round(Number(days) || 0));

export const formatRelativeDays = (days) => {
  const n = wholeDays(days);
  if (n === 0) return "hoy";
  return n === 1 ? "hace 1 día" : `hace ${n} días`;
};

export const formatDuration = (days) => {
  const n = wholeDays(days);
  if (n === 0) return "Mismo día";
  return n === 1 ? "1 día" : `${n} días`;
};

export const rollosWord = (cantidad) => (Number(cantidad) === 1 ? "rollo" : "rollos");

export const formatRollos = (cantidad) => {
  const n = Number(cantidad) || 0;
  return `${n} ${rollosWord(n)}`;
};

// Demora threshold as the server sends it (list diasParaDemora): a positive whole number, or null when the server
// does not send it. The client never assumes a number of days.
export const demoraDays = (value) => {
  const n = Number(value);
  return Number.isInteger(n) && n > 0 ? n : null;
};

// Title of the amber demora tag of one gestión.
export const demoraTitle = (dias) => {
  const days = demoraDays(dias);
  return days ? `Más de ${days} días abierta` : "Gestión demorada";
};

export const formatGestiones = (total) => {
  const n = Number(total) || 0;
  return `${n} ${n === 1 ? "gestión" : "gestiones"}`;
};
